import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const LANGS = [
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const lastScrollY = useRef(0);

  const currentLang = i18n.language?.startsWith("en") ? "en" : "es";

  const navLinks = [
    { label: t("nav.nosotros"), href: "#nosotros" },
    { label: t("nav.talentos"), href: "#talentos" },
    { label: t("nav.trabajo"), href: "#enfoques" },
  ];

  const changeLang = (code: string) => {
    i18n.changeLanguage(code);
    if (typeof document !== "undefined") {
      document.documentElement.lang = code;
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const diff = currentY - lastScrollY.current;

      // Show/hide based on scroll direction
      if (diff > 8 && currentY > 80) {
        setVisible(false);
        setMenuOpen(false);
      } else if (diff < -8) {
        setVisible(true);
      }

      setScrolled(currentY > 40);
      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const LangSwitch = ({ className = "" }: { className?: string }) => (
    <div
      className={`flex items-center gap-0.5 rounded-full p-1 ${className}`}
      style={{ backgroundColor: "rgba(255,255,255,0.08)" }}
    >
      {LANGS.map((lng) => {
        const active = currentLang === lng.code;
        return (
          <button
            key={lng.code}
            onClick={() => changeLang(lng.code)}
            aria-label={`Switch to ${lng.label}`}
            className={`px-3 py-1 rounded-full text-xs font-bold tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
              active ? "bg-brand-pink text-white" : "text-white/50 hover:text-white"
            }`}
          >
            {lng.label}
          </button>
        );
      })}
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        visible ? "translate-y-0" : "-translate-y-full"
      } ${
        scrolled
          ? "backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
      style={scrolled ? { backgroundColor: "rgba(13, 11, 30, 0.92)" } : {}}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        {/* Logo */}
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          className="flex items-center gap-3 cursor-pointer"
        >
          <img
            src="https://static.readdy.ai/image/206e3a23f84eeca55b0a1d27380ad96c/631d2d6717700ea2da3452bcb6d0724c.png"
            alt="301+ Agency Logo"
            className="h-10 w-auto object-contain"
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <LangSwitch />
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap text-white/80 hover:text-white"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNav("#contacto")}
            className="ml-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-brand-pink text-white hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            {t("nav.cta")}
          </button>
        </nav>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden flex items-center justify-center w-10 h-10 cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"} text-white`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden border-t px-6 py-6 flex flex-col gap-4" style={{ backgroundColor: "rgba(13, 11, 30, 0.97)", borderColor: "rgba(255,255,255,0.08)" }}>
          <LangSwitch className="self-start" />
          {navLinks.map((link) => (
            <button
              key={link.href}
              onClick={() => handleNav(link.href)}
              className="text-left text-base font-medium text-white/80 hover:text-white transition-colors cursor-pointer"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNav("#contacto")}
            className="mt-2 px-5 py-3 rounded-full text-sm font-semibold bg-brand-pink text-white hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap text-center"
          >
            {t("nav.cta")}
          </button>
        </div>
      )}
    </header>
  );
}